import { initChildHealthHeartbeat } from './utils/initChildHealthHeartbeat';
import { ALLOWED_GZ_EXACT_HOSTS, ALLOWED_GZ_HOST_SUFFIXES } from './consent-config';
import {
  type IframeChildMessageEvent,
  type IFrameComponent,
  type IframeHealthMessageData,
  IframeMessageType,
  type IframeModalType,
  type IframeParentMessageEvent,
  type ParentIFrameProps,
} from './types';

function injectScript(fn: Function, ...args: any[]) {
  const serializedArgs = args.map(arg => JSON.stringify(arg)).join(',');
  return /*HTML*/ `<script>(${fn.toString()})(${serializedArgs})</script>`;
}

/* Parent scripts */

function setInitialIframeHeight(ocPlusFrameId: string) {
  function setInitialIframeHeight() {
    const gzHeaderId = 'navbar';
    const gzFooterId = 'l-footer';

    const header = document.getElementById(gzHeaderId);
    const footer = document.getElementById(gzFooterId);
    const iframe = document.getElementById(ocPlusFrameId);

    if (header && footer && iframe) {
      const headerHeight = header.offsetHeight;
      const footerHeight = footer.offsetHeight;
      const windowHeight = window.innerHeight;
      const iframeHeight = windowHeight - headerHeight - footerHeight;

      iframe.style.height = iframeHeight + 'px';
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setInitialIframeHeight);
  } else {
    setInitialIframeHeight();
  }
}

function initIframe(ocPlusFrameId: string) {
  const IFRAME_RESIZER_CDN_SCRIPT = 'https://cdn.jsdelivr.net/npm/@iframe-resizer/parent@5.5.7';
  const IFRAME_RESIZER_LICENSE = '12ajjdewwwy-26rnhw2943-1s7g1u8ma0i';
  const OC_PLUS_IFRAME_SELECTOR = `#${ocPlusFrameId}`;
  const gzHeaderId = 'navbar';
  const windowHeight = window.innerHeight;

  const headerHeight = document.getElementById(gzHeaderId)?.offsetHeight || 0;
  const iframeElement = document.getElementById(ocPlusFrameId) as IFrameComponent | null;

  const openLoginModal = () => (document.querySelector('a.account-icon') as HTMLElement | null)?.click();

  const createScrollLock = () => {
    let latestTopOffset = 0;

    const lock = (topOffset: number | undefined) => {
      const topOffsetToUse = topOffset !== undefined ? topOffset : latestTopOffset;

      document.body.style.position = 'fixed';
      document.body.style.top = `-${topOffsetToUse}px`;
      document.body.style.width = '100%';

      latestTopOffset = topOffsetToUse;
    };

    const unlock = (topOffset: number | undefined) => {
      if (document.body.style.position !== 'fixed') return;

      const topOffsetToUse = topOffset !== undefined ? topOffset : latestTopOffset;

      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';

      window.scrollTo(0, topOffsetToUse);

      latestTopOffset = topOffsetToUse;
    };

    return { lock, unlock };
  };

  const createHealthMessageHandler = () => {
    const HEALTH_MISMATCH_THRESHOLD_PX = 120;

    return (iframe: HTMLIFrameElement, data?: IframeHealthMessageData) => {
      const contentHeight = Number(data?.contentHeight || 0);
      if (!contentHeight) return;

      const diff = Math.abs(contentHeight - iframe.offsetHeight);

      if (diff <= HEALTH_MISMATCH_THRESHOLD_PX) return;
      iframe.contentWindow?.location.reload();
    };
  };

  const betslipScrollLock = createScrollLock();
  const filtersScrollLock = createScrollLock();
  const handleHealthMessage = createHealthMessageHandler();

  const onMessage = ({ iframe, message }: IframeChildMessageEvent) => {
    switch (message.type) {
      case IframeMessageType.OPEN_AUTH_MODAL:
        openLoginModal();
        break;
      case IframeMessageType.BETSLIP_OPENED:
        betslipScrollLock.lock(message.data?.topOffset);
        break;
      case IframeMessageType.BETSLIP_CLOSED:
        betslipScrollLock.unlock(message.data?.topOffset);
        break;
      case IframeMessageType.FILTERS_OPENED:
        filtersScrollLock.lock(message.data?.topOffset);
        break;
      case IframeMessageType.FILTERS_CLOSED:
        filtersScrollLock.unlock(message.data?.topOffset);
        break;
      case IframeMessageType.GTM_EVENT:
        // Relay GTM events from iframe to parent's dataLayer
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(message.data);
        break;
      case IframeMessageType.SPA_TAB_CHANGE:
        (window as { trackSPATabChange?: (tabName: string, pathname: string) => void }).trackSPATabChange?.(
          message.data?.tabName,
          message.data?.pathname,
        );
        break;
      case IframeMessageType.IFRAME_HEALTH:
        handleHealthMessage(iframe, message.data);
        break;
    }
  };

  const script = document.createElement('script');
  script.src = IFRAME_RESIZER_CDN_SCRIPT;

  script.onload = () => {
    if (window.iframeResize) {
      window.iframeResize(
        {
          license: IFRAME_RESIZER_LICENSE,
          checkOrigin: false,
          waitForLoad: true,
          onResized(data) {
            if (data.height !== 1) return;
            data.iframe.contentWindow?.location.reload();
          },
          onReady() {
            iframeElement?.iframeResizer?.sendMessage({
              type: IframeMessageType.GET_PARENT_HEIGHTS,
              data: { headerHeight, windowHeight },
            });
          },
          onMessage,
        },
        OC_PLUS_IFRAME_SELECTOR,
      );
    } else {
      console.error('iframeResize not found on window');
    }
  };

  script.onerror = () => {
    console.error('Failed to load iframe-resizer script');
  };

  document.head.appendChild(script);
}

export const OC_PLUS_IFRAME_ID = 'plus-iframe';

export const setInitialIframeHeightScript = injectScript(setInitialIframeHeight, OC_PLUS_IFRAME_ID);
export const initIframeScript = injectScript(initIframe, OC_PLUS_IFRAME_ID);

function initConsentHandler(ocPlusFrameId: string, hostSuffixes: string[], exactHosts: string[]) {
  const iframe = document.getElementById(ocPlusFrameId) as HTMLIFrameElement | null;

  function isAllowedOrigin(origin: string): boolean {
    try {
      const hostname = new URL(origin).hostname;
      return (
        exactHosts.some(h => hostname === h) || hostSuffixes.some(s => hostname === s || hostname.endsWith(`.${s}`))
      );
    } catch {
      return false;
    }
  }

  window.addEventListener(
    'message',
    event => {
      try {
        if (!isAllowedOrigin(event.origin)) return;

        const contentWindow = iframe?.contentWindow;
        if (!contentWindow || event.source !== contentWindow) return;

        if (event.data?.type !== 'FP_CONSENT_REQUEST') return;

        const privacyOK = typeof window.privacyOK === 'boolean' ? window.privacyOK : false;

        contentWindow.postMessage(
          {
            type: 'FP_CONSENT_RESPONSE',
            privacyOK,
            timestamp: Date.now(),
          },
          event.origin,
        );
      } catch {
        // Handler must never throw
      }
    },
    false,
  );
}

export const initConsentHandlerScript = injectScript(
  initConsentHandler,
  OC_PLUS_IFRAME_ID,
  [...ALLOWED_GZ_HOST_SUFFIXES],
  [...ALLOWED_GZ_EXACT_HOSTS],
);

/** Loads GTM only when window.privacyOK === true. Polls for up to 30s if not immediately available. */
function initConsentGatedGTM(gtmId: string) {
  function loadGTM(): boolean {
    if (window.privacyOK !== true) return false;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    script.async = true;
    document.head.appendChild(script);
    return true;
  }

  if (loadGTM()) return;

  const checkInterval = setInterval(() => {
    if (loadGTM()) clearInterval(checkInterval);
  }, 500);

  setTimeout(() => clearInterval(checkInterval), 30000);
}

export function createInitConsentGatedGTMScript(gtmId: string): string {
  return injectScript(initConsentGatedGTM, gtmId);
}

/* Child scripts */

function getDataFromParentProps(parentProps: ParentIFrameProps) {
  const { document, iframe, viewport } = parentProps;

  let topOffset = viewport.pageTop || 0;

  const headerHeight = window.iframeGZContext.parentHeaderHeight || 0;
  const headerAndFooterHeight = document.scrollHeight - iframe.height;

  const footerHeight = headerAndFooterHeight - headerHeight; // Footer height changes when image loads, so it's safer to calculate it this way
  const viewportWithoutFooter = viewport.height - footerHeight;
  const distanceToFooter = iframe.bottom - viewportWithoutFooter - footerHeight;
  const isFooterVisible = distanceToFooter <= 0;

  if (isFooterVisible) topOffset += distanceToFooter; // Reduce topOffset by distance to footer if footer is visible, so we don't cut off any content

  return { topOffset, isFooterVisible, distanceToFooter };
}

const SCROLL_MESSAGES_VARIANTS: Record<IframeModalType, { close: IframeMessageType; open: IframeMessageType }> = {
  betslip: {
    open: IframeMessageType.BETSLIP_OPENED,
    close: IframeMessageType.BETSLIP_CLOSED,
  },
  filters: {
    open: IframeMessageType.FILTERS_OPENED,
    close: IframeMessageType.FILTERS_CLOSED,
  },
};

export function sendGzScrollMessage({ isOpen, variant }: { isOpen: boolean; variant: IframeModalType }) {
  if (typeof window === 'undefined' || !window.parentIFrame) return;

  const disableParentProps = window.parentIFrame.getParentProps(parentProps => {
    disableParentProps();

    const { close, open } = SCROLL_MESSAGES_VARIANTS[variant];
    const { distanceToFooter, isFooterVisible, topOffset: _topOffset } = getDataFromParentProps(parentProps);

    const topOffset = isOpen ? _topOffset : undefined;
    const type = isOpen ? open : close;

    document.documentElement.style.setProperty('--parent-window-top-offset', `${topOffset}px`);

    if (isFooterVisible && isOpen) window.parentIFrame.scrollBy(0, distanceToFooter);
    window.parentIFrame.sendMessage({ type, data: { topOffset } });
  });
}

export function initChildIframeContext() {
  if (typeof window === 'undefined') return;

  window.iframeGZContext = { parentHeaderHeight: 0 };

  window.iFrameResizer = {
    onMessage: (message: IframeParentMessageEvent) => {
      if (message.type === IframeMessageType.GET_PARENT_HEIGHTS) {
        document.documentElement.style.setProperty('--parent-header-height', `${message.data.headerHeight}px`);
        document.documentElement.style.setProperty('--parent-window-height', `${message.data.windowHeight}px`);

        window.iframeGZContext = { ...window.iframeGZContext, parentHeaderHeight: message.data.headerHeight };
      }
    },
  };

  initChildHealthHeartbeat();
}
